<script setup lang="ts">
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import { faAngleDown, faAngleRight } from '@fortawesome/pro-light-svg-icons'
import { getNsItemNumber } from '~/utils/solr/law-utils'
import { buildTocChildren, collectSectionLabels } from '~/utils/toc/ids'

const props = defineProps<{
  node: LawAndCommentary
  partNumber: number
}>()

const route = useRoute()

const { open } = useLawView()

const { isExpanded, setExpanded } = useTocExpandConsumer()

const tocRef = useTemplateRef<HTMLElement>('toc')

const params = useLawRouteParams()
const isStandardText = useIsStandardText()

const showChildren = computed(() => isExpanded(props.node.id))

const filteredChildren = computed(() => buildTocChildren(props.node, isStandardText.value))

const hasCommentary = computed(() => {
  return props.node.ezMainNodeId !== undefined
})

const hasChildren = computed(() => filteredChildren.value.length > 0)

const nsNumber = computed(() => isStandardText.value ? getNsItemNumber(props.node.id) : null)
const displayedTitle = computed(() => {
  const base = props.node.title || `${props.partNumber}. ledd`
  return nsNumber.value ? `${nsNumber.value} ${base}` : base
})

const isChildSelected = computed(() => hasSelectedChild(props.node))

const isNodeOrChildSelected = computed(
  () => isChildSelected.value || params.value.nodeId.toLocaleLowerCase() === props.node.id.toLocaleLowerCase(),
)

function toggleChildren() {
  setExpanded(props.node.id, !showChildren.value)
}

function hasSelectedChild(node: LawAndCommentary) {
  if (!node.children) {
    return false
  }

  for (const child of node.children) {
    if (params.value.nodeId === child.id.toLocaleLowerCase()) {
      return true
    }

    if (hasSelectedChild(child)) {
      return true
    }
  }

  return false
}

const paragraphRange = computed(() => {
  if (props.node.nodeType !== 'chapter') {
    return ''
  }

  const rangeStr = isStandardText.value
    ? buildNsChapterRange(props.node)
    : buildLawChapterRange(props.node)

  if (!rangeStr) {
    return ''
  }
  const hasCommentary = anyDescendantHasEzMainNodeId(props.node)
  return `${rangeStr} ${hasCommentary ? '' : '(uten lovkommentar)'}`
})

function buildLawChapterRange(chapter: LawAndCommentary): string {
  const labels = collectSectionLabels(chapter)
  const min = labels[0]
  const max = labels[labels.length - 1]
  if (!min || !max) {
    return ''
  }
  return min === max ? `§ ${min}` : `§§ ${min} til ${max}`
}

function buildNsChapterRange(chapter: LawAndCommentary): string {
  const sections = chapter.children?.filter(c => c.nodeType === 'section') ?? []
  if (!sections.length) {
    return ''
  }

  // Single punkt with multiple underpunkter → range across the underpunkter
  // ("Punkt 14.1 til 14.8"); otherwise range across the chapter's punkter.
  if (sections.length === 1) {
    const subsections = sections[0]!.children?.filter(c => c.nodeType === 'subsection') ?? []
    if (subsections.length >= 2) {
      const min = getNsItemNumber(subsections[0]!.id)
      const max = getNsItemNumber(subsections[subsections.length - 1]!.id)
      if (min && max) {
        return `Punkt ${min} til ${max}`
      }
    }
    const num = getNsItemNumber(sections[0]!.id)
    return num ? `Punkt ${num}` : ''
  }

  const min = getNsItemNumber(sections[0]!.id)
  const max = getNsItemNumber(sections[sections.length - 1]!.id)
  return min && max ? `Punkt ${min} til ${max}` : ''
}

function anyDescendantHasEzMainNodeId(node: LawAndCommentary): boolean {
  if (node.ezMainNodeId !== undefined) {
    return true
  }

  if (!node.children) {
    return false
  }

  for (const descendant of node.children) {
    if (anyDescendantHasEzMainNodeId(descendant)) {
      return true
    }
  }
  return false
}

function selectNode(event: MouseEvent) {
  // On mobile, switch from TOC to reading view so the section is visible. On desktop, this does nothing
  open('reading', event.currentTarget as HTMLElement)
  navigateTo({ name: 'law-by-id', params: { lovId: props.node.id.split('/').map(segment => segment.toLocaleLowerCase()) }, query: { lcq: route.query?.lcq } })
}

// Only expand the selected path. Do not close siblings, since that can make the list jump.
// `tocRef` makes sure newly mounted levels continue the expansion.
watch([tocRef, isNodeOrChildSelected], ([, isSelected]) => {
  if (isSelected && hasChildren.value) {
    setExpanded(props.node.id, true)
  }
})
</script>

<template>
  <li ref="toc" class="flex flex-col gap-3xs scroll-mt-4xl">
    <div class="flex items-start gap-3xs text-center">
      <button
        v-if="hasChildren"
        :id="node.id"
        class="flex items-center w-8 h-8 px-2xs mt-2xs rounded-lg dark:text-carbon-50 hover:bg-surface-hover dark:hover:bg-surface-hover focus:outline-none focus:ring-blurple-300"
        :class="{ 'bg-surface-primary dark:bg-surface-secondary': showChildren }"
        aria-label="Vis eller skjul underpunkter"
        :aria-expanded="showChildren"
        @click="toggleChildren"
      >
        <Icon :icon="showChildren ? faAngleDown : faAngleRight" />
      </button>
      <div
        v-else
        class="w-8 h-8 px-2xs mt-2xs"
        aria-hidden="true"
        role="presentation"
      />

      <button
        class="text-xsmall rounded-lg flex flex-col w-full justify-start text-left p-2xs dark:text-carbon-50 hover:bg-blurple-50 dark:hover:bg-carbon-400 focus:outline-none focus:ring-blurple-300"
        :class="{
          'bg-surface-hover dark:bg-surface-hover': isNodeOrChildSelected && !isChildSelected,
          'ml-3xs': !hasChildren,
        }"
        :aria-label="displayedTitle"
        @click="selectNode"
      >
        <span>{{ displayedTitle }}  <span v-if="!hasCommentary && props.node.nodeType === 'section'" class="text-secondary text-xs">(uten lovkommentar)</span></span>
        <span v-if="paragraphRange" class="text-secondary dark:text-secondary text-xs block mt-1">{{ paragraphRange }}</span>
      </button>
    </div>
    <ul
      v-if="showChildren && hasChildren"
      class="flex flex-col gap-3xs pl-s"
      :aria-label="node.title || undefined"
    >
      <LawTableOfContent
        v-for="(child, i) in filteredChildren"
        :key="child.id"
        :node="child"
        :part-number="i + 1"
      />
    </ul>
  </li>
</template>
